import { Injectable } from '@angular/core';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { ReviewsActions } from '@actions/index'
import * as fromApp from '@reducers/index'
import { HttpApiService } from '@services/http-api.service';
import { ReviewModel, RequestModel, LocationModel, ReviewPageRequestDescriptor } from '@models/index';
import Swal from 'sweetalert2';
import { RouterFacadeService } from '@services/router-facade.service';
import { GMBFailures } from '@enums/GMBFailures.enum';
import { GMBFacadeService } from '@services/gmb/gmb-facade.service';
import { request } from 'http';
import { error } from 'console';


@Injectable()
export class ReviewsEffects {

    searchReviewsByLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.searchReviewsByLocation),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request).pipe(
                    map((data) => data as { data: ReviewModel[], total: number }),
                    map(data => ReviewsActions.searchReviewsSuccess({ ...data })),
                    catchError(error => of(ReviewsActions.reviewError({ error: error.message }))),
                )
            ),
        )
    );

    searchReviewsByLocationId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.searchReviewsByLocationId),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.getRequest(request).pipe(
                    map((data) => data as { data: ReviewModel[], total: number }),
                    map(data => ReviewsActions.searchReviewsSuccess({ ...data })),
                    catchError(error => of(ReviewsActions.reviewError({ error: error.message }))),
                )
            ),
        )
    );

    getTotalReviewsByLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.getTotalReviewsByLocation),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`,
                })
            ),
            switchMap((request) =>
                this.httpApiService.getRequest(request).pipe(
                    map((data) => data as number),
                    map(total => ReviewsActions.getTotalReviewsByLocationSuccess({ total })),
                    catchError(error => of(ReviewsActions.reviewError({ error: error.message }))),
                )
            ),
        )
    );

    filterReviewsByLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.filterReviewsByLocation),
            map(action => action.request),
            withLatestFrom(
                this.store.select(fromApp.getCurrentLocation),
                (request: RequestModel, location: LocationModel) => ({
                    ...request,
                    params: request.params + `&locationId=${location.id}`
                })
            ),
            switchMap((request) =>
                this.httpApiService.postRequest(request).pipe(
                    map((data) => data as { data: ReviewModel[], total: number }),
                    map(data => ReviewsActions.searchReviewsSuccess({ ...data })),
                    catchError(error => of(ReviewsActions.reviewError({ error: error.message }))),
                )
            ),
        )
    );

    filterReviewsByLocationId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.filterReviewsByLocationId),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request).pipe(
                    map((data) => data as { data: ReviewModel[], total: number }),
                    map(data => ReviewsActions.searchReviewsSuccess({ ...data })),
                    catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                )
            ),
        )
    );

    getAllReviews$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.getAllReviews),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.getRequest(request).pipe(
                    map((data) => data as { data: ReviewModel[], total: number }),
                    map(data => ReviewsActions.searchReviewsSuccess({ ...data })),
                    catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                )
            ),
        )
    );

    getPagesId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.getPagesId),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request).pipe(
                    map(data => data as (ReviewPageRequestDescriptor & { locationId?: string })[]),
                    map(data => ReviewsActions.getPagesIdSuccess({ data })),
                    catchError(error => of(ReviewsActions.reviewError({ error: error.message })))
                )
            )
        )
    );

    deleteReviewReply$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.deleteReviewReply),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request).pipe(
                    tap(data => this.launchAlertMssg(
                        'Reply Deleted',
                        'Your reply was deleted via Google My Business!',
                        'success'
                    )),
                    map((data) => data as ReviewModel),
                    map(data => ReviewsActions.deleteReviewReplySuccess({ data })),
                    catchError((error) => {
                        if (error.error.message == GMBFailures.AuthFailure) {
                            this.GMBFacadeService.authenticateWithGoogle(null)
                            return of(ReviewsActions.reviewError({ error: error.error.message }))
                        }

                        this.launchAlertMssg('Failure', error.message, 'error');
                        return of(ReviewsActions.reviewError({ error: error.message }))
                    }),
                )
            ),
        )
    );

    markReviewSpam$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.markReviewSpam),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        map((data) => data as ReviewModel),
                        tap(data => {
                            const modalTitle = data.isSpam ? "Review Marked as Spam" : "Review Marked as Not Spam"
                            const modalText = data.isSpam ? "This review will be excluded from website widgets and social sharing"
                                : "This review will be included in website widgets and social sharing"
                            this.launchAlertMssg(modalTitle, modalText, 'success')
                        }),
                        map(data => ReviewsActions.markReviewSpamSuccess({ data })),
                        catchError((error) => {
                            this.launchAlertMssg('Failure', error.message, 'error');
                            return of(ReviewsActions.reviewError({ error: error.message }))
                        })
                    )
            )
        )
    )

    replyToReview$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.replyToReview),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request).pipe(
                    tap(data => this.launchAlertMssg(
                        'Reply Posted',
                        'Your reply was posted via Google My Business!',
                        'success'
                    )),
                    map((data) => data as ReviewModel),
                    map(data => ReviewsActions.replyToReviewSuccess({ data })),
                    catchError(error => {
                        if (error.error.message == GMBFailures.AuthFailure) {
                            this.GMBFacadeService.authenticateWithGoogle(null)
                            return of(ReviewsActions.reviewError({ error: error.error.message }))
                        }

                        this.launchAlertMssg('Failure', error.message, 'error');
                        return of(ReviewsActions.reviewError({ error: error.message }))
                    }),
                )
            ),
        )
    );

    generateReplyResponse$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.generateReplyResponse),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.postRequest(request)
                    .pipe(
                        tap(data => this.launchAlertMssg('Reply Generated', 'Your review reply was generated!', 'success')),
                        map((data) => data as any),
                        map(data => ReviewsActions.generateReplyResponseSuccess({ data })),
                        catchError(error => {
                            this.launchAlertMssg('Failure', error.message, 'error');
                            return of(ReviewsActions.reviewError({ error: error.message }))
                        })
                    )
            )
        )
    );

    addOnOpenAi$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ReviewsActions.replyIntegration),
            map(action => action.request),
            switchMap((request) =>
                this.httpApiService.getRequest(request)
                    .pipe(
                        tap(data => this.launchAlertMssg('Request sent successfully', 'Your request to generate reply was send it!', 'success')),
                        map((data) => data as any),
                        map(data => ReviewsActions.replyIntegrationSuccess({ data })),
                        catchError(error => {
                            this.launchAlertMssg('Failure', error.message, 'error');
                            return of(ReviewsActions.reviewError({ error: error.message }))
                        })
                    )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private httpApiService: HttpApiService,
        private store: Store<any>,
        private routerFacadeService: RouterFacadeService,
        private GMBFacadeService: GMBFacadeService
    ) { }

    launchAlertMssg(title: string, message: string, type: any) {
        Swal.fire({
            title: title,
            html: message,
            icon: type,
            showCancelButton: false,
            confirmButtonText: 'OK'
        }).then(result => {
            if (result.value && type == 'error') {
                this.routerFacadeService.navigateTo$('admin-location/location-review-targets')
            }
        });
    }
}
